import JSZip from 'jszip';
import {
  ArrowRight,
  Cpu,
  FileCheck2,
  HardDriveDownload,
  LockKeyhole,
  PackageCheck,
  Play,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ToolIcon } from '../../components/ToolIcon';
import {
  createDefaultOptions,
  toolCategories,
} from '../../domain/tools/registry';
import {
  localize,
  type Locale,
  type OfflinePackId,
  type ToolDefinition,
} from '../../domain/tools/types';
import { useLanguage } from '../language/useLanguage';
import { useOfflinePacks } from '../offline/useOfflinePacks';
import { clearTemporaryTaskData } from '../offline/packManager';
import { markToolUsed } from '../preferences/preferences';
import {
  FILE_VALIDATION_CODES,
  validateFileBytes,
  type FileKind,
  type FileValidationDiagnostic,
} from '../../lib/files/validation';
import { formatBytes, formatNumber, formatPercent } from '../../lib/format';
import { parsePageRange } from '../../lib/pdf/ranges';
import {
  PROCESSING_PROTOCOL_VERSION,
  type ProcessingArtifact,
  type ProcessingResult,
  type SerializableRecord,
  type SerializableValue,
} from '../../processing/contracts';
import { getBrowserProcessingRunner } from '../../processing/browserRunner';
import { ProcessingError } from '../../processing/errors';
import type { ProcessingProgressUpdate } from '../../processing/progress';
import { PdfInputPreview } from './PdfInputPreview';
import {
  FilePicker,
  FileQueue,
  OptionsPanel,
  ProgressPanel,
  ResultPanel,
  type FilePickerIssue,
  type FilePickerSelection,
  type WorkspaceMetricTone,
  type WorkspaceOptionValue,
  type WorkspaceResultMetric,
} from './components';
import './workspace.css';

interface QueuedFile {
  readonly id: string;
  readonly file: File;
  readonly kind: FileKind;
  readonly warnings: readonly FilePickerIssue[];
  readonly pageCount?: number;
}

const extensionKinds: Readonly<Record<string, FileKind>> = {
  '.pdf': 'pdf',
  '.jpg': 'jpeg',
  '.jpeg': 'jpeg',
  '.png': 'png',
  '.webp': 'webp',
  '.html': 'html',
  '.htm': 'html',
  '.docx': 'docx',
  '.pptx': 'pptx',
  '.xlsx': 'xlsx',
};

const canonicalMimeTypes: Readonly<Partial<Record<FileKind, string>>> = {
  pdf: 'application/pdf',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  html: 'text/html',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function canonicalMimeType(kind: FileKind, declaredType: string): string {
  return (canonicalMimeTypes[kind] ?? declaredType) || 'application/octet-stream';
}

function uniqueId(): string {
  return crypto.randomUUID?.() ?? `satr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function expectedKinds(tool: ToolDefinition): readonly FileKind[] {
  return Array.from(new Set(tool.acceptedFileTypes.flatMap(({ extensions }) =>
    extensions.map((extension) => extensionKinds[extension.toLocaleLowerCase('en')])
      .filter((kind): kind is FileKind => kind !== undefined),
  )));
}

function diagnosticMessage(
  diagnostic: FileValidationDiagnostic,
  file: File,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (
    diagnostic.code === FILE_VALIDATION_CODES.extensionMismatch ||
    diagnostic.code === FILE_VALIDATION_CODES.declaredMimeMismatch
  ) {
    return t('errors.file.extensionMismatch', { name: file.name });
  }
  if (diagnostic.code.startsWith('archive.') || diagnostic.code.startsWith('ooxml.')) {
    return t('errors.file.archiveUnsafe', { name: file.name });
  }
  if (diagnostic.code === FILE_VALIDATION_CODES.emptyFile) {
    return t('errors.file.empty', { name: file.name });
  }
  return t('errors.file.unsupportedType', { name: file.name });
}

function fileLooksMemoryHeavy(file: File): boolean {
  const deviceMemory = (navigator as Navigator & { readonly deviceMemory?: number }).deviceMemory ?? 4;
  const practicalBytes = Math.min(768 * 1024 * 1024, Math.max(128, deviceMemory * 96) * 1024 * 1024);
  return file.size > practicalBytes;
}

function requiredPackIds(
  tool: ToolDefinition,
  options: Readonly<Record<string, WorkspaceOptionValue>>,
): readonly OfflinePackId[] {
  const declared = tool.offlinePacks ?? [];
  if (tool.id === 'ocr') {
    const language = String(options.language ?? 'eng+ara');
    return declared.filter((id) =>
      (id === 'ocr-en' && language.includes('eng')) ||
      (id === 'ocr-ar' && language.includes('ara')),
    );
  }
  return declared.filter((id) => id !== 'ocr-en' && id !== 'ocr-ar');
}

function pagesToExpression(pages: readonly number[], pageCount: number | undefined): string {
  if (pages.length === 0) return '';
  if (pageCount !== undefined && pages.length === pageCount) return 'all';
  return pages.join(',');
}

async function readOpfsArtifact(path: string): Promise<ArrayBuffer> {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0 || path.startsWith('/') || path.includes('\\') || parts.includes('..')) {
    throw new ProcessingError('processing.outputInvalid', { reason: 'opfsPath' });
  }
  const storage = navigator.storage as StorageManager & {
    getDirectory?: () => Promise<FileSystemDirectoryHandle>;
  };
  const root = await storage.getDirectory?.();
  if (root === undefined) throw new ProcessingError('processing.storageUnavailable');
  let directory = root;
  for (const part of parts.slice(0, -1)) directory = await directory.getDirectoryHandle(part);
  const fileName = parts.at(-1);
  if (fileName === undefined) throw new ProcessingError('processing.outputInvalid');
  return (await (await directory.getFileHandle(fileName)).getFile()).arrayBuffer();
}

async function artifactBuffer(artifact: ProcessingArtifact): Promise<ArrayBuffer> {
  return artifact.source.kind === 'buffer'
    ? artifact.source.buffer
    : readOpfsArtifact(artifact.source.path);
}

function triggerDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000);
}

function stageDetail(
  progress: ProcessingProgressUpdate,
  t: ReturnType<typeof useTranslation>['t'],
): string | undefined {
  switch (progress.stage) {
    case 'queued': return t('workflow.processing.preparingFiles');
    case 'loading-engine': return t('workflow.processing.loadingEngine');
    case 'writing-output':
    case 'finalizing': return t('workflow.processing.finalizing');
    case 'validating-output': return t('workflow.processing.validating');
    default: return undefined;
  }
}

function localizedError(error: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  if (!(error instanceof ProcessingError)) return t('errors.generic');
  switch (error.code) {
    case 'processing.cancelled':
    case 'processing.workerTerminated': return t('errors.processing.canceled');
    case 'processing.invalidPageRange': return t('errors.pages.invalidRange');
    case 'processing.removeAllPages': return t('errors.pages.removeAll');
    case 'processing.invalidCrop': return t('errors.pages.cropOutsidePage');
    case 'processing.passwordRequired': return t('errors.password.required');
    case 'processing.wrongPassword': return t('errors.password.incorrect');
    case 'processing.invalidPassword': return t('errors.password.weak');
    case 'processing.malformedInput': return t('errors.processing.malformedInput');
    case 'processing.outputInvalid':
    case 'processing.validationFailed': return t('errors.processing.outputInvalid');
    case 'processing.assetMissing': return t('errors.offline.packNotInstalled');
    case 'processing.workerFailed': return t('errors.processing.workerStopped');
    default: return t('errors.processing.failed');
  }
}

function warningText(
  code: string,
  tool: ToolDefinition,
  locale: Locale,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (code.includes('signature')) return t('notices.visualSignature');
  if (code.includes('permission') || code.includes('protect')) return t('notices.permissionEnforcement');
  if (code.includes('redact')) return t('notices.permanentRedaction');
  if (code.includes('pdfa')) return t('notices.pdfaValidation');
  if (code.includes('crop')) return t('notices.croppedContent');
  if (code.includes('compress.light')) return t('workflow.result.noReduction');
  if (code.includes('compress')) return t('notices.strongCompression');
  if (code.includes('translate')) return t('notices.translatedLayout');
  if (code.includes('summary') || code.includes('summar')) return t('notices.aiFallback');
  if (code.includes('office') || code.includes('docx') || code.includes('pptx') || code.includes('xlsx')) {
    return t('notices.officeBestEffort', { format: localize(tool.name, locale) });
  }
  return tool.warnings?.[0] ? localize(tool.warnings[0], locale) : t('errors.processing.unsupportedFeature');
}

function metricLabel(
  key: string,
  t: ReturnType<typeof useTranslation>['t'],
): string | undefined {
  switch (key) {
    case 'inputBytes': return t('workflow.result.originalSize');
    case 'outputBytes': return t('workflow.result.resultSize');
    case 'bytesSaved':
    case 'reductionRatio': return t('workflow.result.spaceSaved');
    case 'pages':
    case 'pagesRemaining': return t('common.labels.pageCount');
    case 'outputs': return t('common.labels.output');
    case 'dpi': return t('common.labels.resolution');
    default: return undefined;
  }
}

function metricValue(key: string, value: SerializableValue, locale: Locale): string | undefined {
  if (typeof value === 'number') {
    if (key.toLocaleLowerCase('en').includes('bytes')) return formatBytes(value, locale);
    if (key === 'reductionRatio') return formatPercent(value, locale);
    return formatNumber(value, locale, { maximumFractionDigits: 2 });
  }
  if (typeof value === 'boolean') return value ? (locale === 'ar' ? 'نعم' : 'Yes') : (locale === 'ar' ? 'لا' : 'No');
  if (typeof value === 'string') return value;
  return undefined;
}

export function ToolWorkspace({ tool }: { readonly tool: ToolDefinition }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const packs = useOfflinePacks();
  const validatedKinds = useRef(new WeakMap<File, FileKind>());
  const activeController = useRef<AbortController | undefined>(undefined);
  const activeJobId = useRef<string | undefined>(undefined);
  const [files, setFiles] = useState<readonly QueuedFile[]>([]);
  const [options, setOptions] = useState<Record<string, WorkspaceOptionValue>>(() => createDefaultOptions(tool));
  const optionsRef = useRef(options);
  const [pageCounts, setPageCounts] = useState<Readonly<Record<string, number>>>({});
  const [selectedPages, setSelectedPages] = useState<readonly number[]>([]);
  const [validatingFiles, setValidatingFiles] = useState(false);
  const [progress, setProgress] = useState<ProcessingProgressUpdate>();
  const [canceling, setCanceling] = useState(false);
  const [result, setResult] = useState<ProcessingResult>();
  const [error, setError] = useState<string>();
  const [downloadBusy, setDownloadBusy] = useState(false);

  const category = toolCategories.find(({ id }) => id === tool.category);
  const pageOption = tool.options.find(({ kind }) => kind === 'page-range');
  const kinds = useMemo(() => expectedKinds(tool), [tool]);
  const supportsAuxiliaryImage = tool.id === 'sign' || tool.id === 'edit-overlay';
  const multiple = tool.multipleFiles || supportsAuxiliaryImage;
  const maximumFiles = tool.id === 'compare' || supportsAuxiliaryImage ? 2 : undefined;
  const previewFile = files.find(({ kind }) => kind === 'pdf');
  const previewPageCount = previewFile ? pageCounts[previewFile.id] : undefined;

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    activeController.current?.abort();
    setFiles([]);
    const defaults = createDefaultOptions(tool);
    setOptions(defaults);
    optionsRef.current = defaults;
    setPageCounts({});
    setSelectedPages([]);
    setProgress(undefined);
    setCanceling(false);
    setResult(undefined);
    setError(undefined);
  }, [tool]);

  useEffect(() => {
    setSelectedPages([]);
  }, [previewFile?.id]);

  useEffect(() => () => {
    activeController.current?.abort();
    void clearTemporaryTaskData();
  }, []);

  const validateFile = useCallback(async (file: File, { signal }: { readonly signal: AbortSignal }) => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (signal.aborted) return [];
    const validation = validateFileBytes(bytes, {
      expectedKinds: kinds,
      fileName: file.name,
      declaredMimeType: file.type,
    });
    validatedKinds.current.set(file, validation.kind);
    const issues: FilePickerIssue[] = validation.diagnostics.map((diagnostic) => ({
      code: diagnostic.code,
      severity: diagnostic.severity,
      message: diagnosticMessage(diagnostic, file, t),
    }));
    if (fileLooksMemoryHeavy(file)) {
      issues.push({
        code: 'file.memoryWarning',
        severity: 'warning',
        message: t('errors.memory.description'),
      });
    }
    return issues;
  }, [kinds, t]);

  const receiveSelection = useCallback((selection: FilePickerSelection) => {
    if (selection.accepted.length === 0) return;
    setError(undefined);
    setFiles((current) => [
      ...current,
      ...selection.accepted.map(({ file, warnings }) => ({
        id: uniqueId(),
        file,
        kind: validatedKinds.current.get(file) ?? 'unknown',
        warnings,
      })),
    ]);
  }, []);

  const handlePageCount = useCallback((fileId: string, pageCount: number) => {
    setPageCounts((current) => ({ ...current, [fileId]: pageCount }));
    setFiles((current) => current.map((entry) =>
      entry.id === fileId ? { ...entry, pageCount } : entry,
    ));
    if (fileId !== previewFile?.id || pageOption === undefined) return;
    const expression = String(optionsRef.current[pageOption.key] ?? pageOption.defaultValue);
    if (expression.trim() === '') {
      setSelectedPages([]);
      return;
    }
    const parsed = parsePageRange(expression, pageCount);
    if (parsed.ok) setSelectedPages(parsed.pages);
  }, [pageOption, previewFile?.id]);

  const handleOptionChange = useCallback((key: string, value: WorkspaceOptionValue) => {
    setOptions((current) => ({ ...current, [key]: value }));
    if (key === pageOption?.key && typeof value === 'string' && previewPageCount !== undefined && value.trim() !== '') {
      const parsed = parsePageRange(value, previewPageCount);
      if (parsed.ok) setSelectedPages(parsed.pages);
    }
  }, [pageOption?.key, previewPageCount]);

  const requiredKeys = useMemo(() => {
    if (tool.id === 'unlock') return ['password'];
    if (tool.id === 'protect') return ['openPassword'];
    if (tool.id === 'remove-pages') return ['pages'];
    if (tool.id === 'html-to-pdf' && files.length === 0) return ['html'];
    return [];
  }, [files.length, tool.id]);

  const optionErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    for (const key of requiredKeys) {
      if (String(options[key] ?? '').trim() === '') {
        errors[key] = key.toLocaleLowerCase('en').includes('password')
          ? t('errors.password.required')
          : key === 'pages' ? t('errors.pages.required') : t('errors.file.missing');
      }
    }
    if (pageOption !== undefined && previewPageCount !== undefined) {
      const expression = String(options[pageOption.key] ?? '');
      if (expression.trim() !== '') {
        const parsed = parsePageRange(expression, previewPageCount);
        if (!parsed.ok) errors[pageOption.key] = t('errors.pages.invalidRange');
      }
    }
    if (tool.id === 'redact' && String(options.searchText ?? '').trim() === '' && String(options.rectangles ?? '').trim() === '') {
      errors.rectangles = t('errors.pages.required');
    }
    return errors;
  }, [options, pageOption, previewPageCount, requiredKeys, t, tool.id]);

  const requiredPacks = useMemo(() => requiredPackIds(tool, options), [options, tool]);
  const missingPacks = useMemo(() => requiredPacks.filter((id) => {
    const definition = packs.manifest?.packs.find((pack) => pack.id === id);
    const installed = packs.installed.get(id);
    return definition === undefined || installed?.version !== definition.version;
  }), [packs.installed, packs.manifest, requiredPacks]);

  const hasInput = files.length > 0 || (tool.id === 'html-to-pdf' && String(options.html ?? '').trim() !== '');
  const minimumFiles = tool.id === 'html-to-pdf' ? 0 : (tool.minimumFiles ?? 1);
  const hasMinimum = files.length >= minimumFiles;
  const hasExactComparePair = tool.id !== 'compare' || files.length === 2;
  const hasPdfWhenNeeded = !tool.acceptedFileTypes.some(({ extensions }) => extensions.includes('.pdf')) ||
    !tool.acceptedFileTypes.some(({ extensions }) => extensions.some((extension) => ['.jpg', '.jpeg', '.png', '.webp'].includes(extension))) ||
    files.some(({ kind }) => kind === 'pdf');
  const canProcess = hasInput && hasMinimum && hasExactComparePair && hasPdfWhenNeeded &&
    Object.keys(optionErrors).length === 0 && missingPacks.length === 0 &&
    progress === undefined && result === undefined && !validatingFiles;

  const run = async () => {
    if (!canProcess) return;
    const controller = new AbortController();
    const jobId = uniqueId();
    activeController.current = controller;
    activeJobId.current = jobId;
    setError(undefined);
    setCanceling(false);
    setProgress({ kind: 'indeterminate', stage: 'queued' });
    try {
      const inputs = await Promise.all(files.map(async ({ id, file, kind }) => {
        const buffer = await file.arrayBuffer();
        return {
          id,
          name: file.name,
          mimeType: canonicalMimeType(kind, file.type),
          byteLength: buffer.byteLength,
          source: { kind: 'buffer' as const, buffer },
        };
      }));
      const processingResult = await getBrowserProcessingRunner().run({
        protocolVersion: PROCESSING_PROTOCOL_VERSION,
        jobId,
        processorId: tool.processingAdapter,
        inputs,
        config: { ...options } satisfies SerializableRecord,
      }, {
        signal: controller.signal,
        hardCancellationTimeoutMs: 1_500,
        onProgress: (event) => setProgress(event),
      });
      setResult(processingResult);
      markToolUsed(tool.id);
      setOptions((current) => Object.fromEntries(tool.options.map((definition) => [
        definition.key,
        definition.sensitive ? definition.defaultValue : current[definition.key] ?? definition.defaultValue,
      ])));
    } catch (caught) {
      setError(localizedError(caught, t));
    } finally {
      activeController.current = undefined;
      activeJobId.current = undefined;
      setProgress(undefined);
      setCanceling(false);
    }
  };

  const cancel = () => {
    if (activeController.current === undefined) return;
    if (!window.confirm(`${t('workflow.processing.cancelConfirmTitle')}\n\n${t('workflow.processing.cancelConfirmDescription')}`)) return;
    setCanceling(true);
    activeController.current.abort();
    if (activeJobId.current !== undefined) getBrowserProcessingRunner().cancel(activeJobId.current);
  };

  const download = async (artifactId: string) => {
    const artifact = result?.artifacts.find(({ id }) => id === artifactId);
    if (artifact === undefined) return;
    try {
      setDownloadBusy(true);
      triggerDownload(new Blob([await artifactBuffer(artifact)], { type: artifact.mimeType }), artifact.name);
    } catch {
      setError(t('errors.download.failed'));
    } finally {
      setDownloadBusy(false);
    }
  };

  const downloadAll = async () => {
    if (result === undefined) return;
    const bundled = result.artifacts.find(({ mimeType }) => mimeType === 'application/zip');
    if (bundled !== undefined) {
      await download(bundled.id);
      return;
    }
    try {
      setDownloadBusy(true);
      const zip = new JSZip();
      for (const artifact of result.artifacts) zip.file(artifact.name, await artifactBuffer(artifact));
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      triggerDownload(blob, `${tool.id}-results.zip`);
    } catch {
      setError(t('errors.download.zipFailed'));
    } finally {
      setDownloadBusy(false);
    }
  };

  const preview = async (artifactId: string) => {
    const artifact = result?.artifacts.find(({ id }) => id === artifactId);
    if (artifact === undefined) return;
    const previewWindow = window.open('about:blank', '_blank');
    if (previewWindow !== null) previewWindow.opener = null;
    try {
      const url = URL.createObjectURL(new Blob([await artifactBuffer(artifact)], { type: artifact.mimeType }));
      if (previewWindow !== null) {
        previewWindow.location.replace(url);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      previewWindow?.close();
      setError(t('errors.download.failed'));
    }
  };

  const startOver = () => {
    activeController.current?.abort();
    setFiles([]);
    const defaults = createDefaultOptions(tool);
    setOptions(defaults);
    optionsRef.current = defaults;
    setPageCounts({});
    setSelectedPages([]);
    setResult(undefined);
    setError(undefined);
    void clearTemporaryTaskData();
  };

  const metrics = useMemo<readonly WorkspaceResultMetric[]>(() => {
    if (result?.metrics === undefined) return [];
    return Object.entries(result.metrics).flatMap(([key, value]) => {
      const label = metricLabel(key, t);
      const display = metricValue(key, value, language);
      if (label === undefined || display === undefined) return [];
      const tone: WorkspaceMetricTone = key === 'bytesSaved' || key === 'reductionRatio' ? 'positive' : 'neutral';
      return [{ id: key, label, value: display, tone }];
    });
  }, [language, result?.metrics, t]);

  const resultWarnings = useMemo(() => result?.warnings?.map(({ code }) =>
    warningText(code, tool, language, t),
  ) ?? [], [language, result?.warnings, t, tool]);

  return (
    <div className="content-wrap tool-workspace-page">
      <nav className="breadcrumbs" aria-label={t('navigation.breadcrumbs')}>
        <Link to="/">{t('navigation.home')}</Link>
        <ArrowRight size={14} aria-hidden="true" />
        <Link to="/tools">{category ? localize(category.name, language) : t('navigation.allTools')}</Link>
        <ArrowRight size={14} aria-hidden="true" />
        <span aria-current="page">{localize(tool.name, language)}</span>
      </nav>

      <header className="tool-workspace-hero">
        <span className="tool-icon"><ToolIcon tool={tool.id} size={27} /></span>
        <div>
          <h1>{localize(tool.name, language)}</h1>
          <p>{localize(tool.description, language)}</p>
        </div>
        <span className="tool-local-badge"><ShieldCheck size={16} aria-hidden="true" />{t('app.privacyPromise')}</span>
      </header>

      <div className="tool-workspace-layout">
        <section className="tool-workspace-card" aria-label={localize(tool.name, language)}>
          {result === undefined && (
            <>
              <div className="tool-local-strip">
                <LockKeyhole size={18} aria-hidden="true" />
                <span><strong>{t('app.localProcessing')}</strong><small>{t('notices.localOnly')}</small></span>
              </div>

              <FilePicker
                acceptedFileTypes={tool.acceptedFileTypes}
                multiple={multiple}
                maxFiles={maximumFiles}
                existingFileCount={files.length}
                existingFileNames={files.map(({ file }) => file.name)}
                busy={progress !== undefined}
                disabled={progress !== undefined}
                capture={tool.id === 'scan' ? 'environment' : undefined}
                validator={validateFile}
                onSelection={receiveSelection}
                onValidationStateChange={setValidatingFiles}
              />

              {files.length > 0 && (
                <FileQueue
                  items={files.map(({ id, file, pageCount, warnings }) => ({
                    id,
                    name: file.name,
                    size: file.size,
                    mimeType: file.type,
                    pageCount,
                    status: warnings.length > 0 ? 'warning' : 'ready',
                    statusLabel: warnings.length > 0 ? t('common.status.ready') : undefined,
                    message: warnings[0]?.message,
                  }))}
                  disabled={progress !== undefined}
                  reorderable={multiple}
                  onReorder={(orderedIds) => setFiles((current) => orderedIds.flatMap((id) => {
                    const entry = current.find((candidate) => candidate.id === id);
                    return entry ? [entry] : [];
                  }))}
                  onRemove={(id) => {
                    setFiles((current) => current.filter((entry) => entry.id !== id));
                    setPageCounts((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== id)));
                  }}
                />
              )}

              {previewFile !== undefined && (
                <PdfInputPreview
                  fileId={previewFile.id}
                  file={previewFile.file}
                  selectedPages={selectedPages}
                  disabled={progress !== undefined}
                  onPageCount={handlePageCount}
                  onSelectionChange={(pages) => {
                    setSelectedPages(pages);
                    if (pageOption !== undefined) {
                      handleOptionChange(pageOption.key, pagesToExpression(pages, previewPageCount));
                    }
                  }}
                />
              )}

              {tool.options.length > 0 && (
                <OptionsPanel
                  definitions={tool.options}
                  values={options}
                  errors={optionErrors}
                  requiredKeys={requiredKeys}
                  disabled={progress !== undefined}
                  onChange={handleOptionChange}
                  onReset={() => {
                    const defaults = createDefaultOptions(tool);
                    setOptions(defaults);
                    optionsRef.current = defaults;
                    setSelectedPages([]);
                  }}
                />
              )}

              {tool.warnings && tool.warnings.length > 0 && (
                <ul className="workspace-messages tool-capability-notes">
                  {tool.warnings.map((warning, index) => (
                    <li key={index} data-tone="warning"><TriangleAlert size={16} aria-hidden="true" />{localize(warning, language)}</li>
                  ))}
                </ul>
              )}

              {missingPacks.length > 0 && (
                <section className="tool-pack-callout" aria-label={t('offline.packs.title')}>
                  <HardDriveDownload size={22} aria-hidden="true" />
                  <div>
                    <strong>{t('offline.packs.title')}</strong>
                    {missingPacks.map((id) => {
                      const definition = packs.manifest?.packs.find((pack) => pack.id === id);
                      const name = definition ? localize(definition.name, language) : id;
                      return <p key={id}>{t('offline.packs.missingForTool', { tool: localize(tool.name, language), pack: name })}</p>;
                    })}
                    {packs.progress && (
                      <p>{t('offline.packs.installProgress', {
                        name: packs.manifest?.packs.find(({ id }) => id === packs.progress?.packId)?.name[language] ?? '',
                        percent: formatNumber(Math.round((packs.progress.loaded / Math.max(1, packs.progress.total)) * 100), language),
                      })}</p>
                    )}
                  </div>
                  <div className="tool-pack-actions">
                    {missingPacks.map((id) => {
                      const definition = packs.manifest?.packs.find((pack) => pack.id === id);
                      return (
                        <button key={id} className="workspace-secondary-button" type="button" disabled={packs.loading || definition?.available === false} onClick={() => void packs.install(id)}>
                          {t('offline.packs.installPack', { name: definition ? localize(definition.name, language) : id })}
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {error && <p className="tool-workspace-error" role="alert"><TriangleAlert size={17} aria-hidden="true" />{error}</p>}

              {progress === undefined ? (
                <div className="tool-workspace-submit">
                  <button className="workspace-primary-button" type="button" disabled={!canProcess} onClick={() => void run()}>
                    <Play size={18} aria-hidden="true" />{t('common.actions.process')}
                  </button>
                  <small><Cpu size={15} aria-hidden="true" />{t('app.localProcessing')}</small>
                </div>
              ) : (
                <ProgressPanel progress={progress} detail={stageDetail(progress, t)} canceling={canceling} onCancel={cancel} />
              )}
            </>
          )}

          {result !== undefined && (
            <>
              {error && <p className="tool-workspace-error" role="alert"><TriangleAlert size={17} aria-hidden="true" />{error}</p>}
              <ResultPanel
                artifacts={result.artifacts.map((artifact) => ({
                  id: artifact.id,
                  name: artifact.name,
                  size: artifact.byteLength,
                  mimeType: artifact.mimeType,
                  detail: artifact.expectedPageCount === undefined ? undefined : t('common.counts.pages', { count: artifact.expectedPageCount }),
                }))}
                metrics={metrics}
                warnings={resultWarnings}
                validationPassed
                busy={downloadBusy}
                onDownload={(id) => void download(id)}
                onDownloadAll={() => void downloadAll()}
                onPreview={(id) => void preview(id)}
                onStartOver={startOver}
              />
            </>
          )}
        </section>

        <aside className="tool-workspace-aside">
          <section>
            <PackageCheck size={19} aria-hidden="true" />
            <h2>{t('toolLibrary.outputFormats')}</h2>
            <p>{tool.outputs.map(({ label }) => localize(label, language)).join(' · ')}</p>
          </section>
          <section>
            <FileCheck2 size={19} aria-hidden="true" />
            <h2>{t('toolLibrary.acceptedFormats')}</h2>
            <p>{tool.acceptedFileTypes.map(({ label }) => localize(label, language)).join(' · ')}</p>
          </section>
          <section>
            <ShieldCheck size={19} aria-hidden="true" />
            <h2>{t('settings.privacy.filesStayLocal')}</h2>
            <p>{t('settings.privacy.filesStayLocalDescription')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
